import React, { useEffect, useState } from "react";
import {
  Comparator,
  dateFilter,
  selectFilter,
  textFilter,
} from "react-bootstrap-table2-filter";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Badge, Card, CardBody, Col, Row } from "reactstrap";
import {
  listNotification,
  resetListNotification,
  setListNotificationColumn,
} from "store/actions";
import { notificationIsRead, notificationModeList } from "./utils";
import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import Breadcrumbs from "components/Common/Breadcrumb";
import DetailNotification from "./DetailNotification";
import queryString from "query-string";

const ListNotification = props => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(["notificationManagement"]);
  const { addToast } = useToasts();
  const { notificationHeaderId = "", notificationHeaderCode = "" } =
    queryString.parse(useLocation().search);

  const [params, setParams] = useState({
    notificationHeaderId,
    notificationHeaderCode,
  });

  const notificationManagement = useSelector(
    state => state.notificationManagement
  );
  const {
    loadingList,
    errorList,
    list: notificationList,
  } = notificationManagement;

  const [mode, setMode] = useState("");
  const [columnsTable, setColumnsTable] = useState([]);

  const [dataDetail, setDataDetail] = useState({});
  const [detailModal, setDetailModal] = useState(false);

  // Action list And Change Language
  useEffect(() => {
    // Set default value to page|sizePerPage when change Language
    if (params.notificationHeaderCode) {
      // Set params to save  - Not send directly
      notificationList.params.page = 1;
      notificationList.params.sizePerPage = 10;
      notificationList.params.columnFilter = {
        ...notificationList.params.columnFilter,
        "filter[code]": params.notificationHeaderCode,
      }
      dispatch(
        listNotification(notificationList.params)
      );
    } else {
      notificationList.params.page = 1;
      notificationList.params.sizePerPage = 10;
      dispatch(
        listNotification(notificationList.params)
      );
    }

    initColumn();
    return () => {
      dispatch(resetListNotification());
    };
  }, [dispatch, t]);

  // When Data Use For Table Changed
  useEffect(() => {
    if (notificationList || params) {
      setColumnsTable(initColumns);
    }

    if (notificationList.columnsVisible) {
      initColumn();
    }
  }, [notificationList, params]);

  // Action Dispatch
  useEffect(() => {
    if (notificationList && notificationList.success) {
      if (params.notificationHeaderCode) {
        // TODO make Notification as Read if not read
        setDataDetail({ notificationId: params.notificationHeaderId });
        setMode(notificationModeList.VIEW_NOTIFICATION_DETAIL);
        toggleDetailModal();

        setParams({
          ...params,
          notificationHeaderCode: "",
          notificationHeaderId: "",
        });
      }
    }
  }, [notificationList, params]);

  // Error Dispatch
  useEffect(() => {
    if (errorList && errorList.message) {
      addToast(errorList.message, {
        appearance: "error",
        autoDismiss: true,
      });
      dispatch(resetListNotification());
    }
  }, [addToast, dispatch, errorList]);

  const initColumn = () => {
    if (notificationList.columnsVisible) {
      for (const item of initColumns) {
        if (notificationList.columnsVisible[item.dataField] !== undefined) {
          item.hidden = !notificationList.columnsVisible[item.dataField];
        }
      }
    }
    setColumnsTable(initColumns);
  };

  const optionShowColumnHandler = (field, value) => {
    dispatch(setListNotificationColumn(field, value));
  };

  const selectOptionsTable = (cell = undefined, name = "", prefix = "") => {
    let options = [];
    const select = {};
    switch (name) {
      case "is_read":
        if (prefix === "notification") {
          options = notificationIsRead;
        }
        break;
      default:
        if (cell) {
          return "";
        } else {
          return select;
        }
    }

    // Set option to select with Array values defined
    options.map(item => {
      select[item.key] = t(`table.${name}.${prefix}.${item.key}`);
    });

    // Use For Column Formatter
    if (cell) {
      return select[cell];
    }

    return { ...select };
  };

  const formatOptionsTable = (cell, name = "", prefix = "") => {
    let options = [];
    switch (name) {
      case "is_read":
        if (prefix === "notification") {
          options = notificationIsRead;
        }
        break;
      default:
        return "";
    }

    return options.map(
      item =>
        item.key === cell && (
          <Badge
            key={item.key}
            className={`font-size-12 ${item.badgeColor ? item.badgeColor : ""}`}
            pill
          >
            {selectOptionsTable(cell, name, prefix)}
          </Badge>
        )
    );
  };

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder, columnFilter }
  ) => {
    notificationList.params.sort = sortField
      ? sortOrder + sortField
      : notificationList.params.sort;
    notificationList.params.columnFilter = columnFilter || {};
    notificationList.params.page = page || 1;
    notificationList.params.sizePerPage = sizePerPage || 10;
    dispatch(listNotification(notificationList.params));
  };

  const onSearchAllHandle = value => {
    notificationList.params.keywords = value;
    dispatch(listNotification(notificationList.params));
  };

  const toggleDetailModal = (modeSet = "") => {
    setDetailModal(!detailModal);
  };

  const refreshNotification = (modeSet = "") => {
    switch (modeSet) {
      case notificationModeList.VIEW_NOTIFICATION_DETAIL:
        dispatch(listNotification(notificationList.params));
        break;
      default:
        break;
    }
  }

  const viewDetailContent = (cell, row, rowIndex) => {
    setDataDetail({ notificationId: row.id });
    setMode(notificationModeList.VIEW_NOTIFICATION_DETAIL);
    toggleDetailModal();
  };

  const rowEventsHandler = {
    onDoubleClick: (e, row, rowIndex) => {
      setDataDetail({ notificationId: row.id });
      setMode(notificationModeList.VIEW_NOTIFICATION_DETAIL);
      toggleDetailModal();
    },
  };

  const listRowAction = [
    // {
    //   icon: <i className="fas fa-pen fa-lg text-primary" />,
    //   text: t("table.actions.edit.employee"),
    //   onClick: clickEditEmployeeHandler,
    // },
    // {
    //   icon: <i className="fas fa-lock fa-lg text-danger" />,
    //   text: t("table.actions.delete.employee"),
    //   conditionVisible: {
    //     record_status: [employeeRecordStatusMode.ACTIVE],
    //   },
    //   onClick: clickDeleteEmployeeHandler,
    // },
    // {
    //   icon: <i className="fas fa-lock-open fa-lg text-success" />,
    //   text: t("table.actions.active.employee"),
    //   conditionVisible: {
    //     record_status: [employeeRecordStatusMode.INACTIVE],
    //   },
    //   onClick: clickActiveEmployeeHandler,
    // },
  ];

  const listMultipleRowAction = [];

  const initColumns = [
    {
      dataField: "code",
      text: t("table.code"),
      sort: true,
      filter: textFilter({
        defaultValue: params.notificationHeaderCode,
        placeholder: t("placeholder.enter", {
          title: t("table.code"),
        }),
      }),
    },
    {
      dataField: "content",
      text: t("table.content"),
      isDummyField: true,
      align: "center",
      formatter: (cell, row, rowIndex) => {
        return (
          <button
            type="button"
            className="btn btn-rounded btn-primary"
            onClick={() => viewDetailContent(cell, row, rowIndex)}
          >
            {t("table.view_detail")}
          </button>
        );
      },
    },
    {
      dataField: "is_read",
      text: t("table.is_read.notification.name"),
      sort: true,
      filter: selectFilter({
        placeholder: t("placeholder.select", {
          title: t("table.is_read.notification.name"),
        }),
        options: selectOptionsTable(undefined, "is_read", "notification"),
      }),
      formatter: (cell, row) => {
        const fakeData = false;
        const dataCheck = fakeData ? "true" : "false";
        // "some" return boolean
        if (notificationIsRead.some(item => item.key === dataCheck)) {
          return formatOptionsTable(dataCheck, "is_read", "notification");
        } else {
          return ""; // Set Empty If Value Not Match
        }
      },
    },
    {
      dataField: "checker_date",
      text: t("table.checker_date"),
      sort: true,
      formatter: (cell, row) => {
        if (cell) {
          return new Intl.DateTimeFormat("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }).format(Number(cell) * 1000);
        } else {
          return "";
        }
      },
      filter: dateFilter({
        defaultValue: { comparator: Comparator.EQ },
      }),
    },
    {
      dataField: "checker_id",
      text: t("table.checker_id"),
      filter: textFilter({
        placeholder: t("placeholder.enter", {
          title: t("table.checker_id"),
        }),
      }),
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content badge-soft ">
        <MetaTags>
          <title>{t("title.list")}</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs
            title={t("title.manager")}
            breadcrumbItem={
              t("title.list") + ` (${notificationList.totalCount})`
            }
          />
          <Row>
            {/* Modal START*/}
            <Col className="col-12">
              {/* Detail Notification Modal */}
              <DetailNotification
                dataDetail={dataDetail}
                isOpenModal={detailModal}
                toggleModal={toggleDetailModal}
                mode={mode}
                refreshNotification={refreshNotification}
              />
            </Col>
            {/* Modal E N D*/}
            <Col className="col-12">
              <Card>
                <CardBody>
                  <RemoteDataTable
                    // selectRow={selectRowCheckbox}
                    // onRowsSelect={onRowsSelect}
                    // expandRow={expandRowTable}
                    keyField="id"
                    data={notificationList.items}
                    columns={columnsTable}
                    page={notificationList.params.page}
                    sizePerPage={notificationList.params.sizePerPage}
                    totalSize={notificationList.totalCount}
                    onTableChange={handleTableChange}
                    onSearchAllHandle={onSearchAllHandle}
                    optionShowColumnHandel={optionShowColumnHandler}
                    rowEvents={rowEventsHandler}
                    listMultipleRowAction={listMultipleRowAction}
                    listRowAction={listRowAction}
                    loading={loadingList}
                  ></RemoteDataTable>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ListNotification;
